import { useState } from "react";

import { capLabel, type McpQuotaDelete } from "../../api/mcp";
import { useAccess } from "../../auth/useAccess";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteFailure } from "../write/WriteFailure";
import { Badge, Card, Notice, Table, Td, Th } from "../ui";

/**
 * Remove one tool's own cap from the tier's `tools`, so the tool falls back to
 * the tier's `tool_default` (or unlimited).
 *
 * `DELETE /admin/quota/{tier}/{tool}` takes no `force` and no body. A 404 means
 * the tier has no cap of its own for that tool — nothing to remove, not a
 * failure. The tier-wide `tool_default` and `max_calls_per_day` are not
 * removed here: the doc says to PATCH them to `null` instead.
 *
 * `now_resolves_to` comes back on the response and is what the tool ACTUALLY
 * falls back to. The success message is computed from that, never from the
 * tier's document, as the doc asks.
 */
export function RemoveOverride({
  config,
  tier,
  caps,
  toolDefault,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  /** The tier's own per-tool caps. Only these can be removed. */
  caps: Record<string, number>;
  /** What a tool without its own cap resolves to, for the dialog's copy. */
  toolDefault: number;
  onDone: () => void;
}) {
  const access = useAccess();
  const write = useWrite<McpQuotaDelete>(config);
  const [target, setTarget] = useState<string | null>(null);
  const [done, setDone] = useState<McpQuotaDelete | null>(null);

  const canWrite = access.can("quota:write");
  const rows = Object.keys(caps).sort((a, b) => a.localeCompare(b));

  function submit() {
    if (!target) return;
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(tier)}/${encodeURIComponent(target)}`, {
        method: "DELETE",
      })
      .then((r) => {
        if (!r) return;
        setDone(r);
        setTarget(null);
        onDone();
      });
  }

  if (rows.length === 0) return null;

  return (
    // A Card, like every other panel. The `text-xs uppercase` heading is the
    // console's field-label style; used as a section title it left this table
    // unbounded on the page, directly under another unbounded table.
    <Card
      title="Remove an override"
      note="A tool with its own cap. Removing it makes the tool resolve through the tier's tool_default again."
      readOnly={!canWrite}
    >
      {done && (
        <div className="mb-4">
          <Notice tone="green" title="Removed">
            <p>
              <span className="ref">{done.tool ?? target}</span> no longer has its own cap on{" "}
              <span className="ref">{done.tier ?? tier}</span>
              {typeof done.previous === "number" && (
                <> — it was {capLabel(done.previous).text.toLowerCase()}</>
              )}
              {typeof done.now_resolves_to === "number" && (
                <>
                  {" "}
                  and now resolves to <strong>{capLabel(done.now_resolves_to).text}</strong>
                </>
              )}
              .
            </p>
            {typeof done.effective_within_seconds === "number" && (
              <p className="mt-1 text-xs">
                Effective within ~{Math.round(done.effective_within_seconds / 60)} minutes — the
                cap cache TTL, not instantly.
              </p>
            )}
          </Notice>
        </div>
      )}

      <Table
        className="[--gutter:0px]"
        head={
          <tr>
            <Th>Tool</Th>
            <Th>Cap</Th>
            <Th right>{null}</Th>
          </tr>
        }
      >
        {rows.map((tool) => {
          const c = capLabel(caps[tool]!);
          return (
            <tr key={tool}>
              <Td>
                <span className="ref">{tool}</span>
              </Td>
              <Td>
                <Badge tone={c.tone}>{c.text}</Badge>
              </Td>
              <Td right>
                {canWrite && (
                  <Button
                    plain
                    type="button"
                    onClick={() => {
                      write.reset();
                      setDone(null);
                      setTarget(tool);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Td>
            </tr>
          );
        })}
      </Table>

      {!canWrite && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Removing a cap needs <span className="ref">quota:write</span>, granted by admin-operator
          or admin-owner.
        </p>
      )}

      <ConfirmDialog
        open={target !== null}
        title={`Remove ${target}'s cap from ${tier}`}
        confirmLabel="Remove the cap"
        busy={write.state.kind === "submitting"}
        error={
          write.state.kind === "notfound" ? (
            <Notice tone="amber" title="No own cap">
              <p>
                Nothing to remove — <span className="ref">{target}</span> has no cap of its own on
                this tier, so it already resolves through the tier&apos;s{" "}
                <span className="ref">tool_default</span>.
              </p>
            </Notice>
          ) : (
            <WriteFailure state={write.state} />
          )
        }
        onConfirm={submit}
        onCancel={() => {
          setTarget(null);
          write.reset();
        }}
      >
        <p className="text-sm">
          <span className="ref">{target}</span> loses its own cap and falls back to this
          tier&apos;s <span className="ref">tool_default</span>, currently{" "}
          <strong>{capLabel(toolDefault).text}</strong>. The response says what it actually
          resolves to.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
